export function parseUrlFilters(parsed, defaultItemsPerPage) {
  const count = Number.parseInt(parsed.count, 10) || defaultItemsPerPage;
  const offset = Number.parseInt(parsed.offset, 10) || 0;
  const page = Math.floor(offset / count) + 1;

  const norm = v => {
    if (Array.isArray(v)) {
      return v;
    }

    return v ? [v] : [];
  };

  const fileTypes = norm(parsed.fileTypes);
  const validationStatuses = norm(parsed.validationStatuses);
  const importStatuses = norm(parsed.importStatuses);
  const search = parsed.search || '';
  const sortOrder = parsed.sortOrder;
  const sortColumn = parsed.sortColumn;
  const uploadedDate = parsed.uploadedDate ? new Date(parsed.uploadedDate) : null;
  const importedDate = parsed.importedDate ? new Date(parsed.importedDate) : null;
  const hasFilters =
    fileTypes.length > 0 ||
    validationStatuses.length > 0 ||
    importStatuses.length > 0 ||
    !!search ||
    !!sortOrder ||
    !!sortColumn ||
    !!uploadedDate ||
    !!importedDate;

  return {
    count,
    page,
    fileTypes,
    validationStatuses,
    importStatuses,
    search,
    sortOrder,
    sortColumn,
    uploadedDate,
    importedDate,
    hasFilters,
  };
}

export function applyUrlFiltersToState({
  fileTypes,
  validationStatuses,
  importStatuses,
  search,
  sortOrder,
  sortColumn,
  uploadedDate,
  importedDate,
  setFileTypeFilter,
  setPlaylistFilter,
  setSequenceFilter,
  setScheduleFilter,
  setAssignmentFilter,
  setTagFilter,
  setValidationStatusTypeFilter,
  setValidStatusFilter,
  setInvalidStatusFilter,
  setImportStatusTypeFilter,
  setImportedStatusFilter,
  setNotImportedStatusFilter,
  setScheduledForImportStatusFilter,
  setFilterSearchField,
  setSortOrderFilter,
  setSortColumnFilter,
  setUploadedDateFilter,
  setImportedDateFilter,
}) {
  setFileTypeFilter(fileTypes);
  setPlaylistFilter(fileTypes.includes('playlist'));
  setSequenceFilter(fileTypes.includes('sequence'));
  setScheduleFilter(fileTypes.includes('schedule'));
  setAssignmentFilter(fileTypes.includes('assignment'));
  setTagFilter(fileTypes.includes('tag'));

  setValidationStatusTypeFilter(validationStatuses);
  setValidStatusFilter(validationStatuses.includes('valid'));
  setInvalidStatusFilter(validationStatuses.includes('invalid'));

  setImportStatusTypeFilter(importStatuses);
  setImportedStatusFilter(importStatuses.includes('imported'));
  setNotImportedStatusFilter(importStatuses.includes('not_imported'));
  setScheduledForImportStatusFilter(importStatuses.includes('scheduled_for_import'));

  setFilterSearchField(search);
  setSortOrderFilter(sortOrder || null);
  setSortColumnFilter(sortColumn || null);
  setUploadedDateFilter(uploadedDate);
  setImportedDateFilter(importedDate);
}

export function buildFilterUrl({
  overrides = {},
  fileTypeFilter,
  validationStatusTypeFilter,
  importStatusTypeFilter,
  filterSearchField,
  sortOrderFilter,
  sortColumnFilter,
  uploadedDateFilter,
  importedDateFilter,
  pathname,
  count,
  getLocalDateString,
}) {
  const fileTypes = 'fileTypes' in overrides ? overrides.fileTypes : fileTypeFilter;
  const validationStatuses =
    'validationStatuses' in overrides ? overrides.validationStatuses : validationStatusTypeFilter;
  const importStatuses =
    'importStatuses' in overrides ? overrides.importStatuses : importStatusTypeFilter;
  const search = 'search' in overrides ? overrides.search : filterSearchField;
  const sortOrder = 'sortOrder' in overrides ? overrides.sortOrder : sortOrderFilter;
  const sortColumn = 'sortColumn' in overrides ? overrides.sortColumn : sortColumnFilter;
  const uploadedDate = 'uploadedDate' in overrides ? overrides.uploadedDate : uploadedDateFilter;
  const importedDate = 'importedDate' in overrides ? overrides.importedDate : importedDateFilter;
  const params = new URLSearchParams();

  if (count) {
    params.set('count', count);
  }

  (fileTypes || []).forEach(t => params.append('fileTypes[]', t));
  (validationStatuses || []).forEach(sv => params.append('validationStatuses[]', sv));
  (importStatuses || []).forEach(sv => params.append('importStatuses[]', sv));

  if (search) {
    params.set('search', search);
  }

  if (sortOrder) {
    params.set('sortOrder', sortOrder);
  }

  if (sortColumn) {
    params.set('sortColumn', sortColumn);
  }

  if (uploadedDate) {
    params.set('uploadedDate', getLocalDateString(uploadedDate));
  }

  if (importedDate) {
    params.set('importedDate', getLocalDateString(importedDate));
  }

  const queryString = params.toString();

  return pathname + (queryString ? `?${queryString}` : '');
}

export async function scheduleImportHelper({
  filesSelected,
  importDates,
  setShowScheduleImport,
  setFullPageLoading,
  setFullPageLoadingTitle,
  setScheduleImportData,
  setDateArr,
  setFilesSelected,
  setErrorTitle,
  setErrorDescription,
  setShowError,
  resetToFirstPage,
  scheduleImport,
}) {
  try {
    setShowScheduleImport(false);
    setFullPageLoading(true);
    setFullPageLoadingTitle('Scheduling Import');

    const {
      success = false,
      title = 'Something Went Wrong',
      description = 'Please refresh the page and try again',
    } = await scheduleImport(filesSelected, importDates);

    if (success) {
      setScheduleImportData([]);
      setDateArr(new Array(30).fill(null));
      setFilesSelected([]);
      await resetToFirstPage();
    } else {
      setFullPageLoading(false);
      setErrorTitle(title);
      setErrorDescription(description);
      setShowError(true);
      await resetToFirstPage();
    }
  } catch (error) {
    setFullPageLoading(false);
    setShowScheduleImport(true, error);
  }
}

export function scheduleClickedHelper({
  filesSelected,
  setErrorTitle,
  setErrorDescription,
  setShowError,
  setScheduleImportData,
  setShowScheduleImport,
}) {
  if (!filesSelected || filesSelected.length === 0) {
    setErrorTitle('Please select a file');
    setErrorDescription('You need to select a file whose import you want to schedule');
    setShowError(true);

    return false;
  }

  setScheduleImportData(filesSelected.map(el => el.fileName));
  setShowScheduleImport(true);

  return true;
}

export function downloadClickedHelper({
  filesSelected,
  setErrorTitle,
  setErrorDescription,
  setShowError,
  setShowConfirmModal,
  setShowConfirmModalTitle,
  setShowConfirmModalSubtitle,
  setConfirmModalData,
  setConfirmModalOnClick,
  setConfirmModalBtnText,
  downloadProceed,
}) {
  if (!filesSelected || filesSelected.length === 0) {
    setErrorTitle('Please select a file');
    setErrorDescription('You need to select the file you"ll love to download');
    setShowError(true);

    return false;
  }

  setShowConfirmModal(true);
  setShowConfirmModalTitle('Download Files');
  setShowConfirmModalSubtitle("You're about to download ");
  setConfirmModalData(filesSelected.map(el => el.fileName));
  setConfirmModalOnClick(() => downloadProceed);
  setConfirmModalBtnText('Proceed');

  return true;
}

export function validateClickedHelper({
  filesSelected,
  setErrorTitle,
  setErrorDescription,
  setShowError,
  setShowConfirmModal,
  setShowConfirmModalTitle,
  setShowConfirmModalSubtitle,
  setConfirmModalData,
  setConfirmModalOnClick,
  setConfirmModalBtnText,
  validateProceed,
}) {
  if (!filesSelected || filesSelected.length === 0) {
    setErrorTitle('Please select a file');
    setErrorDescription('You need to select a file to re-validate');
    setShowError(true);

    return false;
  }

  setShowConfirmModal(true);
  setShowConfirmModalTitle('Re-Validate Files');
  setShowConfirmModalSubtitle("You're about to revalidate ");
  setConfirmModalData(filesSelected.map(el => el.fileName));
  setConfirmModalOnClick(() => validateProceed);
  setConfirmModalBtnText('Proceed');

  return true;
}

export function importClickedHelper({
  filesSelected,
  setErrorTitle,
  setErrorDescription,
  setShowError,
  setShowConfirmModal,
  setShowConfirmModalTitle,
  setShowConfirmModalSubtitle,
  setConfirmModalData,
  setConfirmModalOnClick,
  setConfirmModalBtnText,
  importProceed,
}) {
  if (!filesSelected || filesSelected.length === 0) {
    setErrorTitle('Please select a file');
    setErrorDescription('You need to select a file to import');
    setShowError(true);

    return false;
  }

  setShowConfirmModal(true);
  setShowConfirmModalTitle('Import Files');
  setShowConfirmModalSubtitle("You're about to import");
  setConfirmModalData(filesSelected.map(el => el.fileName));
  setConfirmModalOnClick(() => importProceed);
  setConfirmModalBtnText('Proceed');

  return true;
}

export function deleteClickedHelper({
  filesSelected,
  setErrorTitle,
  setErrorDescription,
  setShowError,
  setShowConfirmModal,
  setShowConfirmModalTitle,
  setShowConfirmModalSubtitle,
  setConfirmModalData,
  setConfirmModalOnClick,
  setConfirmModalBtnText,
  deleteProceed,
}) {
  if (!filesSelected || filesSelected.length === 0) {
    setErrorTitle('Please select a file');
    setErrorDescription('You need to select a file to delete');
    setShowError(true);

    return false;
  }

  setShowConfirmModal(true);
  setShowConfirmModalTitle('Delete Files');
  setShowConfirmModalSubtitle("You're about to delete");
  setConfirmModalData(filesSelected.map(el => el.fileName));
  setConfirmModalOnClick(() => deleteProceed);
  setConfirmModalBtnText('Proceed');

  return true;
}

export async function downloadProceedHelper({
  filesSelected,
  filesRetrieved,
  setShowConfirmModal,
  setFullPageLoading,
  setFullPageLoadingTitle,
  setFilesSelected,
  setFilesRetrieved,
  setErrorTitle,
  setErrorDescription,
  setShowError,
  downloadFiles,
}) {
  setShowConfirmModal(false);
  setFullPageLoading(true);
  setFullPageLoadingTitle(
    `Downloading ${filesSelected.length} ${filesSelected.length > 1 ? 'Files' : 'File'}`,
  );

  const { success = false } = await downloadFiles([...filesSelected]);

  if (success) {
    setFilesSelected([]);
    setFullPageLoading(false);
    setFilesRetrieved(filesRetrieved.map(el => ({ ...el, selected: false })));
  } else {
    setFullPageLoading(false);
    setErrorTitle('Something Went Wrong');
    setErrorDescription('Please refresh the page and try again');
    setShowError(true);
  }
}

export async function validateProceedHelper({
  filesSelected,
  setShowConfirmModal,
  setFullPageLoading,
  setFullPageLoadingTitle,
  setFilesSelected,
  setErrorTitle,
  setErrorDescription,
  setShowError,
  resetToFirstPage,
  validateFiles,
}) {
  setShowConfirmModal(false);
  setFullPageLoading(true);
  setFullPageLoadingTitle(
    `Validating ${filesSelected.length} ${filesSelected.length > 1 ? 'Files' : 'File'}`,
  );

  const {
    success = false,
    title = 'Something Went Wrong',
    description = 'Please refresh the page and try again',
  } = await validateFiles([...filesSelected]);

  if (success) {
    setFilesSelected([]);
    setFullPageLoading(false);
    await resetToFirstPage();
  } else {
    setShowConfirmModal(false);
    setErrorTitle(title);
    setErrorDescription(description);
    setShowError(true);
    setFullPageLoading(false);
    await resetToFirstPage();
  }
}

export async function importProceedHelper({
  filesSelected,
  setShowConfirmModal,
  setFullPageLoading,
  setFullPageLoadingTitle,
  setFilesSelected,
  setErrorTitle,
  setErrorDescription,
  setShowError,
  resetToFirstPage,
  importFiles,
}) {
  setShowConfirmModal(false);
  setFullPageLoading(true);
  setFullPageLoadingTitle(
    `Importing ${filesSelected.length} ${filesSelected.length > 1 ? 'Files' : 'File'}`,
  );

  const {
    success = false,
    title = 'Something Went Wrong',
    description = 'Please refresh the page and try again',
  } = await importFiles([...filesSelected]);

  if (success) {
    setFilesSelected([]);
    await resetToFirstPage();
  } else {
    setFullPageLoading(false);
    setErrorTitle(title);
    setErrorDescription(description);
    setShowError(true);
    await resetToFirstPage();
  }
}

export async function deleteProceedHelper({
  filesSelected,
  filesRetrieved,
  setShowConfirmModal,
  setFullPageLoading,
  setFullPageLoadingTitle,
  setFilesSelected,
  setErrorTitle,
  setErrorDescription,
  setShowError,
  resetToFirstPage,
  deleteFiles,
}) {
  setShowConfirmModal(false);
  setFullPageLoading(true);
  setFullPageLoadingTitle(
    `Deleting ${filesSelected.length} ${filesSelected.length > 1 ? 'Files' : 'File'}`,
  );

  const {
    success = false,
    title = 'Something Went Wrong',
    description = 'Please refresh the page and try again',
  } = await deleteFiles([...filesSelected], filesRetrieved);

  if (success) {
    setFilesSelected([]);
    await resetToFirstPage();
  } else {
    setFullPageLoading(false);
    setErrorTitle(title);
    setErrorDescription(description);
    setShowError(true);
    await resetToFirstPage();
  }
}

export async function addFileHelper({
  filesToUpload,
  fileToUploadNames,
  filesRetrieved,
  setFullPageLoading,
  setFullPageLoadingTitle,
  setShowFileNameModal,
  setFilesToUpload,
  setFileToUploadNames,
  setErrorTitle,
  setErrorDescription,
  setShowError,
  resetToFirstPage,
  uploadFiles,
}) {
  const fileExist = filesToUpload.length > 0;

  if (!fileExist) {
    return;
  }

  const allFileNamesSet =
    fileToUploadNames.filter(el => el.trim() !== '').length === filesToUpload.length;

  if (!allFileNamesSet) {
    return;
  }

  setFullPageLoading(true);
  setFullPageLoadingTitle('Validating Files');
  setShowFileNameModal(false);

  const {
    success = false,
    title = 'Something Went Wrong',
    description = 'Please refresh the page and try again',
  } = await uploadFiles([...filesToUpload], filesRetrieved, fileToUploadNames);

  if (success) {
    setFullPageLoadingTitle('');
    setShowFileNameModal(false);
    setFilesToUpload([]);
    setFileToUploadNames([]);
    await resetToFirstPage();
  } else {
    setFullPageLoading(false);
    setErrorTitle(title);
    setErrorDescription(description);
    setShowError(true);
    setFilesToUpload([]);
    setFileToUploadNames([]);
    await resetToFirstPage();
  }
}

export async function inputChangeHelper({
  filesEvent,
  filesRetrieved,
  setFilesToUpload,
  setFileToUploadNames,
  setFullPageLoading,
  setFullPageLoadingTitle,
  setShowFileNameModal,
  setErrorTitle,
  setErrorDescription,
  setShowError,
  setFilesSelecting,
  uploadFiles,
  validateFileBeforeUpload,
  setTotalNumberOfRows,
  setFilesRetrieved,
}) {
  const prevalidate = validateFileBeforeUpload(Array.from(filesEvent.target.files));

  if (!prevalidate.valid) {
    setErrorTitle('Pre-Upload error');
    setErrorDescription(prevalidate.message);
    setShowError(true);

    return;
  }

  const filesArray = Object.values(filesEvent.target.files);

  setFilesToUpload(filesArray);

  const tempFileNames = filesArray.map(el => el.name);

  setFullPageLoading(true);
  setFullPageLoadingTitle('Validating Files');

  const {
    success = false,
    title = 'Something Went Wrong',
    description = 'Please refresh the page and try again',
    allFiles = [...filesRetrieved],
    totalNumberOfRows: _totalNumberOfRows = 0,
  } = await uploadFiles(filesArray, filesRetrieved, tempFileNames);

  if (success) {
    setFullPageLoading(false);
    setFullPageLoadingTitle('');
    setShowFileNameModal(false);
    setFilesToUpload([]);
    setFileToUploadNames([]);
    setFilesRetrieved(allFiles);
    setTotalNumberOfRows(_totalNumberOfRows);
  } else {
    setFullPageLoading(false);
    setErrorTitle(title);
    setErrorDescription(description);
    setShowError(true);
    setFilesRetrieved(allFiles);
    setTotalNumberOfRows(_totalNumberOfRows);
    setFilesToUpload([]);
    setFileToUploadNames([]);
    setFilesSelecting(false);
  }
}

export async function fetchPageHelper({
  fileTypeFilter,
  validationStatusTypeFilter,
  importStatusTypeFilter,
  filterSearchField,
  uploadedDateFilter,
  importedDateFilter,
  sortColumnFilter,
  sortOrderFilter,
  limit,
  page,
  setFilesRetrieved,
  setTotalNumberOfRows,
  setErrorTitle,
  setErrorDescription,
  setShowError,
  filterFiles,
  fetchFiles,
  getLocalDateString,
  setShowSplashPopup,
  setNoSearchResults = () => {},
  hasFilters: hasFiltersParam,
}) {
  const hasFilters =
    hasFiltersParam ??
    (fileTypeFilter.length > 0 ||
      validationStatusTypeFilter.length > 0 ||
      importStatusTypeFilter.length > 0 ||
      filterSearchField.trim() !== '' ||
      uploadedDateFilter ||
      importedDateFilter ||
      sortColumnFilter ||
      sortOrderFilter);
  let response;

  if (hasFilters) {
    response = await filterFiles(
      fileTypeFilter,
      validationStatusTypeFilter,
      filterSearchField,
      importStatusTypeFilter,
      uploadedDateFilter ? getLocalDateString(uploadedDateFilter) : null,
      importedDateFilter ? getLocalDateString(importedDateFilter) : null,
      sortOrderFilter,
      sortColumnFilter,
      limit,
      page,
    );
  } else {
    response = await fetchFiles(limit, page);
  }

  const {
    success = false,
    transformedData: _filesRetrieved = [],
    totalNumberOfRows: _totalNumberOfRows = 0,
    showOnboarding,
  } = response;

  if (!success) {
    setErrorTitle('Something Went Wrong');
    setErrorDescription('Please refresh the page and try again');
    setShowError(true);

    return;
  }

  if (hasFilters && _filesRetrieved.length === 0) {
    setNoSearchResults(true);

    return;
  }

  setNoSearchResults(false);
  setFilesRetrieved(_filesRetrieved);
  setTotalNumberOfRows(_totalNumberOfRows);

  if (!hasFilters && setShowSplashPopup) {
    setShowSplashPopup(showOnboarding);
  }
}

export function itemCheckedHelper({
  event,
  index,
  filesRetrieved,
  setFilesRetrieved,
  shiftLastIndex,
  setShiftLastIndex,
}) {
  if (event?.shiftKey && shiftLastIndex !== false && shiftLastIndex !== undefined) {
    globalThis.window.getSelection().removeAllRanges();

    const start = Math.min(shiftLastIndex, index);
    const end = Math.max(shiftLastIndex, index);
    const newData = filesRetrieved.map((file, i) => ({
      ...file,
      selected: i >= start && i <= end ? true : file.selected,
    }));

    setFilesRetrieved(newData);

    return;
  }

  const isMac = /Mac/.test(navigator.userAgent);
  const modifierKey = event?.[isMac ? 'metaKey' : 'ctrlKey'];

  if (modifierKey) {
    const toggled = !filesRetrieved[index].selected;
    const newData = filesRetrieved.map((file, i) => ({
      ...file,
      ...(i === index ? { selected: toggled, selectedTime: Date.now() } : {}),
    }));

    setFilesRetrieved(newData);

    if (setShiftLastIndex) {
      setShiftLastIndex(toggled ? index : false);
    }

    return;
  }

  const newData = filesRetrieved.map((file, i) => ({
    ...file,
    selected: i === index,
    ...(i === index ? { selectedTime: Date.now() } : {}),
  }));

  setFilesRetrieved(newData);

  if (setShiftLastIndex) {
    setShiftLastIndex(index);
  }
}

export function fileNameChecboxChangedHelper({
  event,
  index,
  filesToUpload,
  fileToUploadNames,
  setFileToUploadNames,
}) {
  const state = event.target.checked;
  const fileNames = [...fileToUploadNames];

  if (state) {
    fileNames[index] = filesToUpload[index].name;
  } else {
    fileNames[index] = '';
  }

  setFileToUploadNames(fileNames);
}
